import {
  DataGridPremium,
  GridColDef,
  GridColumns,
  GridFilterItem,
  GridFilterModel,
  GridLinkOperator,
  GridRowParams,
  itIT,
  useGridApiRef,
  GridSortModel,
  GridCellEditCommitParams
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import {
  ROLE_SUPER_ADMIN,
  ROLE_TENANT_ADMIN,
  ROLE_TENANT_TECHNICIAN
} from './../../../../constants';
import { getPaginationDefault, setPaginationDefault } from 'features/paginationHelper';

import { BaseService } from 'services/api/BaseService';
import { EnhancedToolbarProvider } from './EnhancedToolbarProvider';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { onEditCellCallbackProvider } from './utils';
import toast from 'features/toast';
import { useAppSelector } from 'app/store';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationDeleteTableDialog from 'components/helpers/ConfirmationGridDelete';

/** Column fields that represent warehouse filtering in API query params. */
function isWarehouseScopedColumnField(field: string | undefined): boolean {
  if (!field) return false;
  if (field === 'warehouses') return true;
  return field.includes('warehouse');
}

interface EnhancedDataGridProps {
  service: BaseService<any>;
  columns: GridColDef[] | GridColumns;
  getMethod?: (pageSize: number, offset: number, params: Record<string, string>) => Promise<any>;
  initialParams?: Record<string, string>;
  refresh?: MutableRefObject<any>;
  rows?: MutableRefObject<any>;
  onDataLoaded?: (items: any[], meta: { count: number }) => void;
  outerParams?: any;
  fullTextQuery?: string;
  clientSide?: boolean;
  initialFilterItems?: GridFilterItem[];
  initialOptions?: Partial<GridInitialStatePremium>;
  dense?: boolean;
  checkboxSelection?: boolean;
  disableMultipleSelection?: boolean;
  selectionModelChange?: any;
  getDetailPanelContent?: any;
  disableDelete?: boolean;
  disableEdit?: boolean;
  disableExport?: boolean;
  disableFilter?: boolean;
  height?: string | number;
  onCellEditCommitOverride?: (params: GridCellEditCommitParams, event: any, apiRef: any) => void;
}

function EnhancedDataGrid(props: EnhancedDataGridProps) {
  const {
    service,
    columns,
    getMethod,
    initialParams,
    refresh,
    rows,
    onDataLoaded,
    outerParams,
    fullTextQuery,
    clientSide,
    initialFilterItems,
    initialOptions,
    dense,
    checkboxSelection,
    disableMultipleSelection,
    selectionModelChange,
    getDetailPanelContent,
    disableDelete,
    disableEdit,
    disableExport,
    disableFilter,
    height,
    onCellEditCommitOverride
  } = props;

  const { t } = useTranslation();

  const groups = useAppSelector((state) => state.auth.groups);
  const startingPageSize = getPaginationDefault();
  const apiRef = useGridApiRef();

  const [skip, setSkip] = useState(false);
  const [pageSize, setPageSize] = useState(startingPageSize || 10);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [items, setItems] = useState<any[]>([]);
  const [orderMacro, setOrderMacro] = useState('');
  const [filter, setFilter] = useState<Record<string, string>>(null);

  const defaultWarehouse = useAppSelector((state) => state.auth.warehouse);

  const hasAssignedWarehouse = Boolean(
    defaultWarehouse != null && String(defaultWarehouse).trim().length > 0
  );
  const technicianWithWarehouse =
    Boolean(groups?.includes(ROLE_TENANT_TECHNICIAN)) && hasAssignedWarehouse;

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: []
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [preferenceKey, setPreferenceKey] = useState('');

  /**
   * DataGrid visualization preferences.
   * Save settings to local storage given a `preferenceKey`
   * and apply them on load.
   */

  // Apply saved settings
  const [firstRender, setFirstRender] = useState(true);
  useEffect(() => {
    if (firstRender) {
      // if it's the first render, change the state to trigger the useState
      setFirstRender(false);
    } else {
      refreshColumnsData();
    }
  }, [
    pageSize,
    page,
    fullTextQuery,
    orderMacro,
    filter,
    outerParams,
    defaultWarehouse,
    technicianWithWarehouse,
    filterModel.items,
    sortModel
  ]);
  useEffect(() => {
    if (firstRender === false) {
      const columnFields = columns.map((col) => col.field).filter(Boolean);
      const pk = btoa(
        JSON.stringify({
          service: service.modulePrefix,
          getMethod: getMethod?.name ?? 'default',
          columns: columnFields
        })
      );
      setPreferenceKey(pk);
      const datagridState = localStorage.getItem(pk);
      if (datagridState) {
        try {
          const parsed = JSON.parse(datagridState);
          const savedFields: string[] = parsed?.columns?.orderedFields ?? [];
          const fieldsMatch =
            savedFields.length === columnFields.length &&
            savedFields.every((f, i) => f === columnFields[i]);
          if (fieldsMatch) {
            apiRef.current.restoreState(parsed);
          } else {
            localStorage.removeItem(pk);
            refreshColumnsData();
          }
        } catch {
          localStorage.removeItem(pk);
          refreshColumnsData();
        }
      } else {
        refreshColumnsData();
      }
    }
  }, [firstRender]);

  // Export current state to local storage
  const exportState = useCallback(() => {
    if (apiRef && preferenceKey) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(
        preferenceKey,
        JSON.stringify({
          columns: currentState.columns,
          filter: currentState.filter,
          sorting: currentState.sorting
        })
      );
    }
  }, [apiRef, preferenceKey]);

  // Save applied filters
  useEffect(() => {
    exportState();
  }, [filterModel.items, sortModel, exportState]);

  const refreshColumnsData = () => {
    if (!isLoading) {
      setIsLoading(true);
      let searchParams = {};
      filterModel.items.map((element) => {
        searchParams = { ...searchParams, ..._getFilterObj(element) };
      });

      if (!skip && initialParams) {
        searchParams = initialParams;
        setSkip(true);
      }

      if (!skip && initialFilterItems) {
        let filters = {};
        initialFilterItems.forEach((element) => {
          filters = { ...filters, ..._getFilterObj(element) };
        });
        searchParams = { ...searchParams, ...filters };
      }

      // Solo tecnico con magazzino assegnato: forza il parametro API (colonna Magazzino non filtrabile).
      // Altrimenti `auth.warehouse` sovrascriverebbe il filtro "contiene" (es. "Mestre" → codice completo).
      if (defaultWarehouse && technicianWithWarehouse) {
        const warehouseColumn = columns.find((col) => col.field.includes('warehouse'));
        if (warehouseColumn) {
          const fieldKey = warehouseColumn.field;
          searchParams = { ...searchParams, [fieldKey]: defaultWarehouse };
        }
      }
      if (fullTextQuery) {
        searchParams['q'] = fullTextQuery;
      }
      if (orderMacro.trim().length !== 0) {
        searchParams['ordering'] = orderMacro;
      }
      if (filter) {
        searchParams = { ...searchParams, ...filter };
      }
      if (outerParams) {
        searchParams = { ...searchParams, ...outerParams };
      }
      if (getMethod) {
        getMethod(pageSize, page * pageSize, searchParams)
          .then((res) => {
            const results = res.results;
            const mapped = results.map((item) => {
              if (!item?.id) return { ...item, id: uuidv4() };
              // stock_distinct_per_location: più righe per stesso id → usare row_id come chiave
              if (item.row_id != null) return { ...item, id: item.row_id };
              return item;
            });
            setItems(mapped);
            setCount(res.count);
            if (onDataLoaded) onDataLoaded(mapped, { count: res.count });
            setIsLoading(false);
          })
          .catch(() => {
            searchParams = {};
            setIsLoading(false);
            setFilter(null);
            setPage(0);
          });
      } else {
        service
          .getAllPaginated(pageSize, page * pageSize, searchParams)
          .then((res) => {
            setItems(res.results);
            setCount(res.count);
            if (onDataLoaded) onDataLoaded(res.results, { count: res.count });
            setIsLoading(false);
          })
          .catch(() => {
            searchParams = {};
            setIsLoading(false);
            setFilter(null);
            setPage(0);
          });
      }
    }
  };

  useEffect(() => {
    if (refresh) refresh.current = refreshColumnsData;
    const startingPageSize = getPaginationDefault();
    if (startingPageSize) setPageSize(startingPageSize);
  }, []);

  useEffect(() => {
    if (rows) rows.current = items;
  }, [items]);

  useEffect(() => {
    if (pageSize) {
      setPaginationDefault(pageSize);
    }
  }, [pageSize]);

  useEffect(() => {
    setPage(0);
  }, [fullTextQuery, filter, orderMacro]);

  const onSortingChange = (x) => {
    setSortModel(x);
    if (x.length > 0) setOrderMacro(`${x[0].sort === 'desc' ? '-' : ''}${x[0].field}`);
    else setOrderMacro('');
  };

  const onFilterChange = (x) => {
    setFilterModel(x);
    let filters = {};
    if (!skip && initialParams) filters = initialParams;
    x.items.map((element) => {
      filters = { ...filters, ..._getFilterObj(element) };
    });
    setFilter(filters);
    setSkip(true);
  };

  const handleOnDelete = (id: number) => {
    service.delete(id).then(() => {
      refreshColumnsData();
      toast.success(t('actions.successfully.deleted'));
    });
  };

  function _getFilterObj(item): Record<string, string> {
    if (['contains', '=', 'is'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return { [item.columnField]: item.value };
    } else if (['equals'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return { [`${item.columnField}`]: item.value, [`${item.columnField}__exact`]: true };
    } else if (['startsWith'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return { [`${item.columnField}`]: item.value, [`${item.columnField}__startswith`]: true };
    } else if (['endsWith'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return {
        [`${item.columnField}`]: item.value,
        [`${item.columnField}__endswith`]: true
      };
    } else if (['>', 'after'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return { [`${item.columnField}__gt`]: item.value };
    } else if (['>=', 'onOrAfter'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return { [`${item.columnField}__gte`]: item.value };
    } else if (['<', 'before'].includes(item.operatorValue) && item.value?.trim()?.length > 0) {
      return { [`${item.columnField}__lt`]: item.value };
    } else if (
      ['<=', 'onOrBefore'].includes(item.operatorValue) &&
      item.value?.trim()?.length > 0
    ) {
      return { [`${item.columnField}__lte`]: item.value };
    } else if (['isEmpty'].includes(item.operatorValue)) {
      return { [`${item.columnField}__isnull`]: 'true' };
    } else if (['isNotEmpty'].includes(item.operatorValue)) {
      return { [`${item.columnField}__isnull`]: 'false' };
    } else {
      return null;
    }
  }

  const onEditCell = disableEdit
    ? undefined
    : (params: GridCellEditCommitParams, event: any) => {
        if (onCellEditCommitOverride) {
          return onCellEditCommitOverride(params, event, apiRef);
        }
        return onEditCellCallbackProvider(service, refresh)(params, event);
      };

  const _columns = columns.map((col) => {
    const lockWarehouseFilter = technicianWithWarehouse && isWarehouseScopedColumnField(col.field);
    return {
      ...col,
      editable: disableEdit ? false : col.editable,
      ...(lockWarehouseFilter ? { filterable: false } : {})
    };
  });

  const warehouseColumnForInitialSeed = columns.find(
    (col) => defaultWarehouse && isWarehouseScopedColumnField(col.field)
  );
  const warehouseFilterSeedItems =
    warehouseColumnForInitialSeed && !technicianWithWarehouse
      ? [
          {
            columnField: warehouseColumnForInitialSeed.field,
            operatorValue: 'contains',
            value: defaultWarehouse as string
          }
        ]
      : [];

  if (
    !!service &&
    service.canDelete &&
    !disableDelete &&
    groups &&
    groups.some((group) => [ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN].includes(group))
  ) {
    _columns.push({
      field: '__grid_delete__',
      type: 'actions',
      // headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        return [
          <ConfirmationDeleteTableDialog
            key={999}
            id={params.row['id']}
            handleDelete={handleOnDelete}
          />
        ];
      },
      filterable: false
    });
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Hide multi link operator input */}
      <style>{'.MuiDataGrid-filterFormLinkOperatorInput{display:none}'}</style>
      <DataGridPremium
        apiRef={apiRef}
        initialState={{
          filter: {
            filterModel: {
              items: [...(initialFilterItems || []), ...warehouseFilterSeedItems],
              linkOperator: GridLinkOperator.And,
              quickFilterLogicOperator: GridLinkOperator.And
            }
          },
          pinnedColumns: {
            left: [],
            right: []
          },
          ...(initialOptions || {})
        }}
        localeText={{
          ...itIT.components.MuiDataGrid.defaultProps.localeText,
          filterValueTrue: t('global.yes'),
          filterValueFalse: t('global.no')
        }}
        sx={{ border: 'none', height: height ?? '75vh' }}
        disableSelectionOnClick
        pagination
        density={dense ? 'compact' : 'standard'}
        columns={_columns}
        pageSize={pageSize}
        loading={isLoading}
        page={page}
        rows={items}
        onPageChange={(page) => {
          setPage(page);
        }}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[5, 10, 20, 50, 100, 500, 1000, 2000, 5000]}
        rowCount={count}
        paginationMode={clientSide ? 'client' : 'server'}
        sortingMode={clientSide ? 'client' : 'server'}
        filterMode={clientSide ? 'client' : 'server'}
        onSortModelChange={onSortingChange}
        onFilterModelChange={onFilterChange}
        onColumnOrderChange={exportState}
        onColumnWidthChange={exportState}
        onColumnVisibilityModelChange={exportState}
        sortModel={sortModel}
        // onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        filterModel={filterModel}
        // onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
        components={{
          Toolbar: outerParams?.warehouse_id
            ? EnhancedToolbarProvider(
                service,
                +outerParams.warehouse_id,
                outerParams,
                disableEdit,
                disableExport,
                disableFilter
              )
            : EnhancedToolbarProvider(
                service,
                null,
                outerParams,
                disableEdit,
                disableExport,
                disableFilter
              )
        }}
        onCellEditCommit={onEditCell}
        checkboxSelection={checkboxSelection}
        disableMultipleSelection={disableMultipleSelection}
        onSelectionModelChange={selectionModelChange}
        getDetailPanelHeight={() => 'auto'}
        getDetailPanelContent={getDetailPanelContent ?? null}
      />
    </div>
  );
}

export default EnhancedDataGrid;
